import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { ConectorLightWizard } from "@/components/conector/ConectorLightWizard";
import { PortalPage } from "@/components/portal/PortalPage";
import { PortalFiltersSheet } from "@/components/portal/PortalFiltersSheet";
import { PortalFiltersSheetProvider } from "@/contexts/portal-filters-sheet";
import { getConectorLightDemanda } from "@/lib/conector-light.functions";


export const Route = createFileRoute("/conector-light/{-$id}")({
  validateSearch: (s: Record<string, unknown>) => ({
    editar: typeof s.editar === "string" ? s.editar : undefined,
  }),
  head: ({ params }) => ({
    meta: [
      {
        property: "og:url",
        content: params.id ? `/conector-light/${params.id}` : "/conector-light",
      },
    ],
    links: [
      {
        rel: "canonical",
        href: params.id ? `/conector-light/${params.id}` : "/conector-light",
      },
    ],
  }),
  component: ConectorRoute,
});

function ConectorRoute() {
  const { id } = Route.useParams();
  const { editar } = Route.useSearch();
  const forceEdit = editar === "1";

  // Si el id aparece DESPUÉS del mount inicial (el wizard acaba de crear la
  // demanda y sincronizó la URL), no volvemos a comprobar el estado ni
  // remontamos: sabemos que la demanda es nueva y el wizard sigue vivo.
  const hadIdOnMountRef = useRef(!!id);
  const initialIdRef = useRef<string | undefined>(id);

  const [estado, setEstado] = useState<string | null | undefined>(
    hadIdOnMountRef.current ? undefined : null,
  );
  const [referencia, setReferencia] = useState<string | null>(null);

  useEffect(() => {
    if (!hadIdOnMountRef.current) return;
    if (!id) return;
    if (id !== initialIdRef.current) return;
    // Flujo light: la demanda es pública (no requiere autenticación).
    getConectorLightDemanda({ data: { id } })
      .then((dem) => {
        setEstado(dem?.estado ?? null);
        setReferencia(dem?.referencia ?? null);
      })
      .catch(() => setEstado(null));
  }, [id]);

  // Fresh page load con /conector-light/$id → esperar a saber el estado.
  if (hadIdOnMountRef.current && estado === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B1120]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Demanda ya iniciada → portal de resultados (solo en carga inicial con id).
  if (hadIdOnMountRef.current && estado === "iniciada" && !forceEdit && id) {
    return (
      <PortalFiltersSheetProvider>
        <PortalPage
          segments={[]}
          mode="demanda"
          demandaId={id}
          demandaRef={referencia}
          basePath={`/conector-light/${id}`}
          light
        />
        <PortalFiltersSheet />
      </PortalFiltersSheetProvider>
    );
  }

  // Wizard: se mantiene montado aunque se añada el id a la URL.
  return <ConectorLightWizard initialDemandaId={initialIdRef.current} />;
}
